import { useEffect, useState, useCallback, useRef } from 'react';
import { Button, Card, Tag, Modal, Input, message, Tooltip, Space, Popconfirm, Form, Select, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/request';
import { isCancel } from 'axios';
import { useAuthStore } from '../../stores/auth';
import './MapList.css';

interface MapItem {
  id: string;
  userId: string;
  mapName: string;
  mapData?: string | null;
  thumbnail?: string | null;
  fileSize?: number;
  status: number;
  isPublished: number;
  publishedUrl?: string | null;
  frozenReason?: string | null;
  frozenTime?: string | null;
  createTime: string;
  updateTime: string;
  deleted: number;
}

interface PageResult {
  records: MapItem[];
  total: number;
  current: number;
  size: number;
}

export default function MapList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [maps, setMaps] = useState<MapItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingMap, setEditingMap] = useState<MapItem | null>(null);
  const [newMapName, setNewMapName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | undefined>(undefined);
  const [current, setCurrent] = useState(1);
  const [pageSize] = useState(12);
  const [total, setTotal] = useState(0);
  const [form] = Form.useForm();

  const { quota } = useAuthStore();
  const abortControllerRef = useRef<AbortController | null>(null);

  const loadMaps = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', current.toString());
      params.append('pageSize', pageSize.toString());
      if (searchKeyword) params.append('keyword', searchKeyword);
      if (statusFilter !== undefined) params.append('status', statusFilter.toString());

      const res = await api.get<PageResult>(`/maps?${params.toString()}`, {
        signal: abortControllerRef.current.signal,
      });
      setMaps(res.records || []);
      setTotal(res.total || 0);
    } catch (error: any) {
      if (isCancel(error)) return;
      console.error('Failed to load maps:', error);
      setMaps([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [current, pageSize, searchKeyword, statusFilter]);

  useEffect(() => {
    loadMaps();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadMaps]);

  const handleCreateMap = async () => {
    if (!newMapName.trim()) {
      message.warning(t('maps.createMapModal.mapNameRequired'));
      return;
    }

    setUploading(true);
    try {
      const res = await api.post<any>('/maps', { mapName: newMapName });
      // api.post 返回的是 { code, data: { id, mapName, ... }, ... } 格式
      const mapId = res?.data?.id || res?.id;
      if (mapId) {
        message.success(t('maps.createMapModal.createSuccess'));
        setCreateModalVisible(false);
        setNewMapName('');
        loadMaps();
        navigate(`/editor/${mapId}`);
      } else {
        message.error(t('maps.createMapModal.createFailed'));
      }
    } catch (error) {
      message.error(t('maps.createMapModal.createFailed'));
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateMap = async () => {
    if (!editingMap) return;

    try {
      await form.validateFields();
      const mapName = form.getFieldValue('mapName');

      await api.put(`/maps/${editingMap.id}/name`, { mapName });
      message.success(t('maps.editMapModal.updateSuccess'));
      setEditModalVisible(false);
      setEditingMap(null);
      loadMaps();
    } catch (error) {
      // Form validation error or API error
    }
  };

  const handleDeleteMap = async (mapId: string) => {
    try {
      await api.delete(`/maps/${mapId}`);
      message.success(t('maps.deleteSuccess'));
      loadMaps();
    } catch (error) {
      message.error(t('maps.deleteFailed'));
    }
  };

  const remainingMaps = quota?.remainingMaps ?? -1;
  const canCreateMap = quota !== null && (remainingMaps === -1 || remainingMaps > 0);

  const getQuotaText = () => {
    if (!quota) return t('maps.loading');
    const planName = quota.planName || 'Free';
    const used = quota.usedMaps;
    const total = quota.totalMaps;

    if (remainingMaps === 0) {
      const template = total === -1
        ? t('maps.mapLimitReachedUnlimited')
        : t('maps.mapLimitReached');
      return template.replace('{used}', String(used)).replace('{total}', String(total)).replace('{plan}', planName);
    }
    if (remainingMaps === -1) {
      const template = t('maps.mapsRemainingUnlimited');
      return template.replace('{used}', String(used)).replace('{plan}', planName);
    }
    const template = t('maps.mapsRemaining');
    return template
      .replace('{remaining}', String(remainingMaps))
      .replace('{used}', String(used))
      .replace('{total}', String(total))
      .replace('{plan}', planName);
  };

  const getStatusTag = (status: number) => {
    switch (status) {
      case 1:
        return <Tag color="green">{t('maps.normal')}</Tag>;
      case 0:
        return <Tag color="orange">{t('maps.frozen')}</Tag>;
      default:
        return <Tag color="red">{t('maps.deleted')}</Tag>;
    }
  };

  const getPublishedTag = (isPublished: number) => {
    return isPublished
      ? <Tag color="blue">{t('maps.published')}</Tag>
      : <Tag color="default">{t('maps.draft')}</Tag>;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="map-list-container">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">{t('maps.title')}</h1>
          <p className="text-gray-500 mt-1">{getQuotaText()}</p>
        </div>
        {canCreateMap && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            {t('maps.createNew')}
          </Button>
        )}
      </div>

      <Card className="mb-4">
        <div className="flex gap-4 items-center">
          <Input
            placeholder={t('maps.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={searchKeyword}
            onChange={(e) => {
              setSearchKeyword(e.target.value);
              setCurrent(1);
            }}
            className="w-64"
            allowClear
          />
          <Select
            placeholder={t('maps.filterStatus')}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setCurrent(1);
            }}
            allowClear
            className="w-40"
          >
            <Select.Option value={1}>{t('maps.statusNormal')}</Select.Option>
            <Select.Option value={0}>{t('maps.statusFrozen')}</Select.Option>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="text-gray-500">{t('maps.loading')}</div>
        </div>
      ) : maps.length === 0 ? (
        <Card>
          <Empty
            description={
              canCreateMap
                ? t('maps.noMapsHint')
                : t('maps.mapLimitReachedHint')
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            {canCreateMap && (
              <Button type="primary" onClick={() => setCreateModalVisible(true)}>
                {t('maps.createFirst')}
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        <>
          <div className="map-grid">
            {maps.map((map) => (
              <Card
                key={map.id}
                hoverable
                className="map-card"
                actions={[
                  <Tooltip title={t('maps.openEditor')} key="edit">
                    <Button type="text" icon={<EditOutlined />} onClick={() => navigate(`/editor/${map.id}`)} />
                  </Tooltip>,
                  <Popconfirm
                    title={t('maps.deleteConfirm')}
                    onConfirm={() => handleDeleteMap(map.id)}
                    okText={t('maps.yes')}
                    cancelText={t('maps.no')}
                    key="delete"
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>,
                ]}
              >
                <Card.Meta
                  title={
                    <div className="mb-3">
                      <span className="truncate font-semibold text-base">{map.mapName}</span>
                    </div>
                  }
                  description={
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {getStatusTag(map.status)}
                        {getPublishedTag(map.isPublished)}
                      </div>
                      <div className="space-y-1 text-xs text-gray-500">
                        <div className="flex justify-between">
                          <span>{t('maps.size')}:</span>
                          <span className="font-medium">{formatFileSize(map.fileSize || 0)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{t('maps.created')}:</span>
                          <span className="font-medium">{new Date(map.createTime).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  }
                />
              </Card>
            ))}
          </div>

          {total > pageSize && (
            <div className="flex justify-center mt-6">
              <Space>
                <Button
                  disabled={current === 1}
                  onClick={() => setCurrent(current - 1)}
                >
                  {t('maps.previous')}
                </Button>
                <span className="px-4 py-2">
                  {t('maps.page', { current, total: Math.ceil(total / pageSize) })}
                </span>
                <Button
                  disabled={current >= Math.ceil(total / pageSize)}
                  onClick={() => setCurrent(current + 1)}
                >
                  {t('maps.next')}
                </Button>
              </Space>
            </div>
          )}
        </>
      )}

      <Modal
        title={t('maps.createMapModal.title')}
        open={createModalVisible}
        onOk={handleCreateMap}
        onCancel={() => {
          setCreateModalVisible(false);
          setNewMapName('');
        }}
        confirmLoading={uploading}
        okText={t('maps.createMapModal.okText')}
        cancelText={t('maps.cancel')}
      >
        <div className="py-4">
          <label className="block text-sm font-medium mb-2">{t('maps.createMapModal.mapNameLabel')}</label>
          <Input
            value={newMapName}
            onChange={(e) => setNewMapName(e.target.value)}
            placeholder={t('maps.createMapModal.mapNamePlaceholder')}
            size="large"
          />
          <p className="text-sm text-gray-500 mt-2">
            {t('maps.createMapModal.hint')}
          </p>
        </div>
      </Modal>

      <Modal
        title={t('maps.editMapModal.title')}
        open={editModalVisible}
        onOk={handleUpdateMap}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingMap(null);
          form.resetFields();
        }}
        okText={t('maps.editMapModal.okText')}
        cancelText={t('maps.cancel')}
      >
        <div className="py-4">
          <Form form={form} layout="vertical">
            <Form.Item
              name="mapName"
              label={t('maps.editMapModal.mapNameLabel')}
              rules={[{ required: true, message: t('maps.editMapModal.mapNameRequired') }]}
            >
              <Input placeholder={t('maps.editMapModal.mapNamePlaceholder')} size="large" />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </div>
  );
}
